import { action, observable, runInAction } from 'mobx';
import { BaseStore } from 'src/App/Infrastructure';
import AlertType from 'src/Shared/Alert/AlertType';
import { StringResources } from 'src/Shared/Constants';
import { StockDetailTableModel, TableRowLevel } from './StockDetailTableModel';
import {
  StockLevelForReorderReportResponse,
  StockLevelForReorderReportRequest,
} from '@vulcan/inventory-api-client/lib/models';
import {
  ProductSalesHistoryRequest,
  ProductSalesHistoryResponse,
} from '@vulcan/sales-api-client/esm/models';
import { forEach, groupBy, sumBy } from 'lodash';
import moment from 'moment';
import { roundForDisplay, toFixedNumber } from 'src/Shared/Utils';
import {
  ReorderEntryResponse,
  ReorderQuantityRequest,
  RequestReorderQuantitiesListRequest,
  RequestReorderQuantityRequest,
  DeleteReorderQuantitiesListRequest,
  DeleteReorderQuantityRequest,
  MarkProductEntriesAsReviewed,
  ProcurementViewSearchRequest,
  ProcurementLinesSearchResponse,
  ProcurementLine,
} from '@vulcan/purchasing-api-client/esm/models';
import { RestResponse } from '@azure/ms-rest-js';
import { StockTableModel } from '../StockTable/StockTableModel';

export class StockDetailTableStore extends BaseStore {
  @observable public tableDataLoading = false;
  @observable public tableData: StockDetailTableModel[] = [];
  @observable public expanded = {};
  @observable public row: StockTableModel | undefined;

  @action
  public setRequestedQuantity(
    salesProfitCentreCode: string,
    index: number,
    requestedQuantity: string
  ): void {
    const targetTable = this.tableData.find((d) => d.salesBranchCode === salesProfitCentreCode);
    if (targetTable) {
      const targetTableData = targetTable.children;
      targetTableData[index] = {
        ...targetTableData[index],
        requestedQty: requestedQuantity !== '' ? Number(requestedQuantity) : null,
        isEdited: true,
      };
    }
  }

  @action
  public setComment(
    salesProfitCentreCode: string,
    index: number,
    comment: string
  ): void {
    const targetTable = this.tableData.find((d) => d.salesBranchCode === salesProfitCentreCode);
    if (targetTable) {
      const targetTableData = targetTable.children;
      targetTableData[index] = {
        ...targetTableData[index],
        comment: comment,
        isEdited: true,
      };
    }
  }

  private async saveRequestedQuantity(
    requestedQtyItems: StockDetailTableModel[]
  ): Promise<RestResponse> {
    const purchasingClient = await this.getPurchasingClient();
    const request: RequestReorderQuantitiesListRequest = {
      entries: requestedQtyItems.map(
        (item) =>
        ({
          productId: item.productId,
          branchCode: item.salesBranchCode ?? item.stockBranchCode,
          productGroupName: item.productGroupName,
          requestedQuantity: item.requestedQty,
          comment: item.comment
        } as RequestReorderQuantityRequest)
      ),
    };
    return purchasingClient.reorder.requestReorderQuantity(request);
  }

  private async deleteRequestedQuantity(
    requestedDeleteItems: StockDetailTableModel[]
  ): Promise<RestResponse> {
    const purchasingClient = await this.getPurchasingClient();
    const deleteRequest: DeleteReorderQuantitiesListRequest = {
      entries: requestedDeleteItems.map(
        (item) =>
        ({
          productId: item.productId,
          branchCode: !!item.salesBranchCode ? item.salesBranchCode : item.stockBranchCode,
        } as DeleteReorderQuantityRequest)
      ),
    };
    return purchasingClient.reorder.deleteReorderQuantity(deleteRequest);
  }

  private async reviewedRequestedProduct(
    reviewedItems: StockDetailTableModel[]
  ): Promise<RestResponse> {
    const reviewedProducts = reviewedItems.map((item) => ({
      productId: item.productId,
      branchCode: !!item.salesBranchCode ? item.salesBranchCode : item.stockBranchCode,
      productGroupName: item.productGroupName
    }));
    const reviewedRequest = {
      reviewedProducts: reviewedProducts,
    } as MarkProductEntriesAsReviewed;

    const purchasingClient = await this.getPurchasingClient();
    return purchasingClient.reorder.markProductEntriesAsReviewedMethod(reviewedRequest);
  }

  @action
  public async saveChangesAndMarkReviewed(): Promise<void> {
    try {
      this.tableDataLoading = true;
      const requestedQtyItems: StockDetailTableModel[] = [];
      const requestedDeleteItems: StockDetailTableModel[] = [];
      const reviewedItems: StockDetailTableModel[] = [];

      this.tableData.forEach((data) => {
        data.children.forEach((tableDataEntry) => {
          if (tableDataEntry.isEdited) {
            if (tableDataEntry.requestedQty && tableDataEntry.requestedQty > 0) {
              requestedQtyItems.push(tableDataEntry);
            } else {
              requestedDeleteItems.push(tableDataEntry);
            }
          }

          if (tableDataEntry.showRequestedQtyInput) {
            reviewedItems.push(tableDataEntry);
          }
        });
        const requestedQty = sumBy(data.children, (d) => d.requestedQty || 0);
        data.requestedQty = requestedQty > 0 ? requestedQty : null;
      });

      const requestedQtyPromise = this.saveRequestedQuantity(requestedQtyItems);
      const requestedDeletePromise = this.deleteRequestedQuantity(requestedDeleteItems);
      await Promise.all([requestedQtyPromise, requestedDeletePromise]);
      await this.reviewedRequestedProduct(reviewedItems);
      this.showAlert(AlertType.success, StringResources.SavedReorderQuantity);
    } catch (e) {
      this.log(StringResources.ErrorSavingReorderQuantityException, e as Error);
      this.showAlert(AlertType.danger, StringResources.ErrorSavingReorderQuantityException);
    } finally {
      runInAction(() => {
        this.tableDataLoading = false;
      });
    }
  }

  private async getStockLevelData(
    productId: number
  ): Promise<StockLevelForReorderReportResponse[]> {
    const inventoryClient = await this.getInventoryClient();
    const options: StockLevelForReorderReportRequest = { productIds: [productId] };
    const stockResponse = await inventoryClient.availableStock.getStockForReorderReport(options);
    return stockResponse._response.parsedBody;
  }

  private async getStockSalesData(productId: number): Promise<ProductSalesHistoryResponse[]> {
    if (productId <= 0) {
      return [];
    }

    const salesClient = await this.getSalesClient();
    const options: ProductSalesHistoryRequest = { productIds: [productId] };
    const salesHistoryResponse = await salesClient.dailySales.getProductSalesHistory(options);
    const salesHistoryList = salesHistoryResponse._response.parsedBody;
    return salesHistoryList;
  }

  private async getAllRequestData(productId: number): Promise<ReorderEntryResponse[]> {
    const purchasingClient = await this.getPurchasingClient();
    const options: ReorderQuantityRequest = { productIds: [productId] };
    const requestedEntriesResponse = await purchasingClient.reorder.getRequestedReorderQuantity(
      options
    );
    const response = requestedEntriesResponse._response.parsedBody;
    return response.filter((r) => r.requestedQuantity && r.requestedQuantity > 0);
  }

  private async getAllProcurementData(
    productCode: string
  ): Promise<ProcurementLinesSearchResponse> {
    const purchasingClient = await this.getPurchasingClient();

    const req = {
      querySettings: {
        filterText: '',
        searchText: `ProductCodes=[${productCode}]`,
        orderBy: '',
      },
      pageSettings: {
        skip: 0,
        top: 1000000,
      },
    } as ProcurementViewSearchRequest;

    return purchasingClient.procurementLines.search(req);
  }

  @action
  public updateExpanded(expanded: object): void {
    this.expanded = expanded;
  }

  @action
  public updateChildExpanded(newChildExpanded: object, index: number): void {
    const newExpanded = this.expanded;
    newExpanded[index] = newChildExpanded;
    this.expanded = newExpanded;
  }

  @action
  public async populateTableControl(row: StockTableModel): Promise<void> {
    try {
      this.tableDataLoading = true;
      this.tableData = [];
      this.expanded = {};
      this.row = row;
      const productId = row.productId;

      if (productId > 0) {
        const [stockLevelData, stockSalesData, requestData, procurementData] = await Promise.all([
          this.getStockLevelData(productId),
          this.getStockSalesData(productId),
          this.getAllRequestData(productId),
          this.getAllProcurementData(row.productCode),
        ]);

        // We want to preprocess the data to set the sale profit centres here?
        const tableData = this.getTableData(
          stockLevelData,
          stockSalesData,
          requestData,
          procurementData
        );
        const expanded = {};
        if (!this.GetDetailsPageCollapseAllByDefault) {
          let ind = 0;
          tableData.forEach((element) => {
            const children = element.children;
            children.forEach((child) => {
              expanded[ind] = child;
              ind++;
            });
          });
        }
        runInAction(() => {
          this.tableData = tableData;
          this.expanded = expanded;
        });
      }
    } catch (e) {
      this.log(StringResources.ErrorPopulateStockLevelTable, e as Error);
      this.showAlert(AlertType.danger, StringResources.ErrorPopulateStockLevelTable);
    } finally {
      runInAction(() => {
        this.tableDataLoading = false;
      });
    }
  }

  private getTableData(stockLevelData: StockLevelForReorderReportResponse[],
    stockSalesData: ProductSalesHistoryResponse[],
    requestedQtyData: ReorderEntryResponse[],
    procurementData: ProcurementLinesSearchResponse
  ): StockDetailTableModel[] {
    // only get stock level data under warehouse level row
    const allStockLevel = this.getStockLevelTableModelRows(stockLevelData);
    // only get stock sales data under warehouse level row
    const allStockSales = this.getStockSalesTableModelRows(stockSalesData);
    // merge stock level and sales data
    const allStockInfo = this.mergeStockLevelAndSalesData(allStockLevel, allStockSales);

    const tableRows = this.getSalesProfitCentreLevelRows(allStockInfo, requestedQtyData, procurementData, allStockLevel);
    // sorting at the front sorts No Sales to a place where it wouldn't make sense, so let's sort in here (alternatively we implement a custom sorting function)
    tableRows.sort((left, right) => {
      if (left.salesBranchCode === 'No Sales') {
        return 1; // No Sales should be at the end
      }
      return left.salesBranchCode.localeCompare(right.salesBranchCode);
    });

    return tableRows;
  }

  private getStockLevelTableModelRows(stockLevelData: StockLevelForReorderReportResponse[]): StockDetailTableModel[] {
    const allStockLevel: StockDetailTableModel[] = [];

    const groupStockLevelData = groupBy(stockLevelData, item => `${item.productCode}-${item.warehouseCode}`);
    forEach(groupStockLevelData, (group: StockLevelForReorderReportResponse[]) => {
      const keyItem = group[0];

      const availableStock = sumBy(group, g => g.inStock - g.committed);
      const availableSerialisedStock = sumBy(group, g => g.serialisedInStock - g.serialisedCommitted);
      const availableNonSerialisedStock = sumBy(group, g => g.nonSerialisedInStock - g.nonSerialisedCommitted);
      const onOrder = sumBy(group, g => g.onOrder);
      const committed = sumBy(group, g => g.committed);
      const total = availableStock + onOrder;

      let groupEta: string | null = null;
      let groupEtaStatus = '';
      forEach(group, item => {
        // take latest ETA
        if (item.eta && item.etaStatus !== '' && (!groupEta || moment(item.eta).isBefore(moment(groupEta)))) {
          groupEta = item.eta;
          groupEtaStatus = item.etaStatus;
        }
      });

      allStockLevel.push({
        productId: keyItem.productId,
        productCode: keyItem.productCode,
        productGroupName: keyItem.productGroupName,
        salesProfitCentreCode: 'Stock Level does not have sales information',
        salesBranchCode: 'Stock Level does not have sales information',
        stockBranchCode: keyItem.branchCode,
        stockWarehouseCode: keyItem.warehouseCode,
        stockClass: {
          code: keyItem.stockClassificationCode,
          color: keyItem.stockClassificationColour,
        },

        availableStock: roundForDisplay(availableStock),
        availableNonSerialStock: roundForDisplay(availableNonSerialisedStock),
        availableSerialStock: roundForDisplay(availableSerialisedStock),
        onOrder: roundForDisplay(onOrder),
        committed: roundForDisplay(committed),
        total: roundForDisplay(total),
        eta: groupEta,
        etaStatus: groupEtaStatus,

        averageMonthSales: null,
        thisMonth: null,
        oneMonthAgo: null,
        twoMonthsAgo: null,
        threeMonthsAgo: null,
        fourMonthsAgo: null,
        fiveMonthsAgo: null,
        sixMonthsAgo: null,
        sevenMonthsAgo: null,
        eightMonthsAgo: null,
        nineMonthsAgo: null,
        tenMonthsAgo: null,
        elevenMonthsAgo: null,

        findMatched: false,
        isEdited: false,
        children: [] as StockDetailTableModel[],
        levelNumber: TableRowLevel.WarehouseLevelRow,
        requestedQty: null,
        comment: null,
        showRequestedQtyInput: false,
      } as StockDetailTableModel);
    });
    return allStockLevel;
  }

  private getStockSalesTableModelRows(stockSalesData: ProductSalesHistoryResponse[]): StockDetailTableModel[] {
    const allStockSales: StockDetailTableModel[] = [];
    // if we only group by {item.productCode}-${item.warehouseCode}, will missing sales history from different sales branches
    const groupStockSalesData = groupBy(stockSalesData, (item) => `${item.productCode}-${item.warehouseCode}-${item.salesBranchCode}`);

    forEach(groupStockSalesData, (group: ProductSalesHistoryResponse[]) => {
      const keyItem = group[0];

      const averageMonthSales = sumBy(group, g => g.averageMonthSales || 0);
      const thisMonth = sumBy(group, g => g.thisMonth || 0);
      const oneMonthAgo = sumBy(group, g => g.oneMonthAgo || 0);
      const twoMonthsAgo = sumBy(group, g => g.twoMonthsAgo || 0);
      const threeMonthsAgo = sumBy(group, g => g.threeMonthsAgo || 0);
      const fourMonthsAgo = sumBy(group, g => g.fourMonthsAgo || 0);
      const fiveMonthsAgo = sumBy(group, g => g.fiveMonthsAgo || 0);
      const sixMonthsAgo = sumBy(group, g => g.sixMonthsAgo || 0);
      const sevenMonthsAgo = sumBy(group, g => g.sevenMonthsAgo || 0);
      const eightMonthsAgo = sumBy(group, g => g.eightMonthsAgo || 0);
      const nineMonthsAgo = sumBy(group, g => g.nineMonthsAgo || 0);
      const tenMonthsAgo = sumBy(group, g => g.tenMonthsAgo || 0);
      const elevenMonthsAgo = sumBy(group, g => g.elevenMonthsAgo || 0);

      allStockSales.push({
        productId: keyItem.productId,
        productCode: keyItem.productCode,
        salesProfitCentreCode: keyItem.salesProfitCentreCode,
        salesBranchCode: keyItem.salesBranchCode,
        stockBranchCode: 'Stock Sales does not have stock branch information',
        stockWarehouseCode: keyItem.warehouseCode,

        availableStock: null,
        availableNonSerialStock: null,
        availableSerialStock: null,
        onOrder: null,
        committed: null,
        total: null,

        averageMonthSales: roundForDisplay(averageMonthSales),
        thisMonth: roundForDisplay(thisMonth),
        oneMonthAgo: roundForDisplay(oneMonthAgo),
        twoMonthsAgo: roundForDisplay(twoMonthsAgo),
        threeMonthsAgo: roundForDisplay(threeMonthsAgo),
        fourMonthsAgo: roundForDisplay(fourMonthsAgo),
        fiveMonthsAgo: roundForDisplay(fiveMonthsAgo),
        sixMonthsAgo: roundForDisplay(sixMonthsAgo),
        sevenMonthsAgo: roundForDisplay(sevenMonthsAgo),
        eightMonthsAgo: roundForDisplay(eightMonthsAgo),
        nineMonthsAgo: roundForDisplay(nineMonthsAgo),
        tenMonthsAgo: roundForDisplay(tenMonthsAgo),
        elevenMonthsAgo: roundForDisplay(elevenMonthsAgo),

        findMatched: false,
        requestedQty: null,
        remainingQty: null,
        showRequestedQtyInput: false,
        isEdited: false,
        children: [] as StockDetailTableModel[],
        levelNumber: TableRowLevel.WarehouseLevelRow,
      } as StockDetailTableModel);
    });

    return allStockSales;
  }

  private mergeStockLevelAndSalesData(allStockLevel: StockDetailTableModel[], allStockSales: StockDetailTableModel[]): StockDetailTableModel[] {
    const output: StockDetailTableModel[] = [];
    allStockLevel.forEach((stockLevel) => {
      const matchedStockSalesList = allStockSales.filter(
        stockSales => stockSales.productCode === stockLevel.productCode
          && stockSales.stockWarehouseCode === stockLevel.stockWarehouseCode);

      // this is special case that we want all sales branch has a row that warehouse code same as sales branch code even no sales data
      const noStockBranchSales = matchedStockSalesList.every(stocksales => stocksales.salesBranchCode !== stockLevel.stockBranchCode);
      if (noStockBranchSales) {
        const noSalesRow = {
          productId: stockLevel.productId,
          productCode: stockLevel.productCode,
          productGroupName: stockLevel.productGroupName,
          salesProfitCentreCode: allStockSales.find(sales => sales.salesBranchCode === stockLevel.stockBranchCode)?.salesProfitCentreCode ?? 'No Sales',
          salesBranchCode: stockLevel.stockBranchCode, // use stock branch code as sales branch code when no sales data
          stockBranchCode: stockLevel.stockBranchCode,
          stockWarehouseCode: stockLevel.stockWarehouseCode,
          stockClass: stockLevel.stockClass,

          availableStock: stockLevel.availableStock,
          availableNonSerialStock: stockLevel.availableNonSerialStock,
          availableSerialStock: stockLevel.availableSerialStock,
          onOrder: stockLevel.onOrder,
          committed: stockLevel.committed,
          total: stockLevel.total,

          eta: stockLevel.eta,
          etaStatus: stockLevel.etaStatus,

          averageMonthSales: null,
          thisMonth: null,
          oneMonthAgo: null,
          twoMonthsAgo: null,
          threeMonthsAgo: null,
          fourMonthsAgo: null,
          fiveMonthsAgo: null,
          sixMonthsAgo: null,
          sevenMonthsAgo: null,
          eightMonthsAgo: null,
          nineMonthsAgo: null,
          tenMonthsAgo: null,
          elevenMonthsAgo: null,

          findMatched: false,
          requestedQty: null,
          remainingQty: null,
          showRequestedQtyInput: false,
          isEdited: false,
          children: [] as StockDetailTableModel[],
          levelNumber: TableRowLevel.WarehouseLevelRow,
        } as StockDetailTableModel;
        output.push(noSalesRow);
      }

      // stock level and sales data matched
      if (matchedStockSalesList.length > 0) {
        matchedStockSalesList.forEach(matchedStockSales => {
          output.push({
            productId: stockLevel.productId,
            productCode: stockLevel.productCode,
            productGroupName: stockLevel.productGroupName,
            salesProfitCentreCode: matchedStockSales.salesProfitCentreCode,
            salesBranchCode: matchedStockSales.salesBranchCode,
            stockBranchCode: stockLevel.stockBranchCode,
            stockWarehouseCode: stockLevel.stockWarehouseCode,
            stockClass: stockLevel.stockClass,
            isStockBranchDifferentFromSalesBranch: stockLevel.stockBranchCode !== matchedStockSales.salesBranchCode,

            availableStock: stockLevel.availableStock,
            availableNonSerialStock: stockLevel.availableNonSerialStock,
            availableSerialStock: stockLevel.availableSerialStock,
            onOrder: stockLevel.onOrder,
            committed: stockLevel.committed,
            total: stockLevel.total,

            eta: stockLevel.eta,
            etaStatus: stockLevel.etaStatus,

            averageMonthSales: matchedStockSales.averageMonthSales,
            thisMonth: matchedStockSales.thisMonth,
            oneMonthAgo: matchedStockSales.oneMonthAgo,
            twoMonthsAgo: matchedStockSales.twoMonthsAgo,
            threeMonthsAgo: matchedStockSales.threeMonthsAgo,
            fourMonthsAgo: matchedStockSales.fourMonthsAgo,
            fiveMonthsAgo: matchedStockSales.fiveMonthsAgo,
            sixMonthsAgo: matchedStockSales.sixMonthsAgo,
            sevenMonthsAgo: matchedStockSales.sevenMonthsAgo,
            eightMonthsAgo: matchedStockSales.eightMonthsAgo,
            nineMonthsAgo: matchedStockSales.nineMonthsAgo,
            tenMonthsAgo: matchedStockSales.tenMonthsAgo,
            elevenMonthsAgo: matchedStockSales.elevenMonthsAgo,

            findMatched: true,
            requestedQty: null,
            remainingQty: null,
            showRequestedQtyInput: false,
            isEdited: false,
            children: [] as StockDetailTableModel[],
            levelNumber: TableRowLevel.WarehouseLevelRow,
          } as StockDetailTableModel);
        });
      } else {
        const noSalesRow = {
          productId: stockLevel.productId,
          productCode: stockLevel.productCode,
          productGroupName: stockLevel.productGroupName,
          salesProfitCentreCode: 'No Sales',
          salesBranchCode: stockLevel.stockBranchCode, // use stock branch code as sales branch code when no sales data
          stockBranchCode: stockLevel.stockBranchCode,
          stockWarehouseCode: stockLevel.stockWarehouseCode,
          stockClass: stockLevel.stockClass,

          availableStock: stockLevel.availableStock,
          availableNonSerialStock: stockLevel.availableNonSerialStock,
          availableSerialStock: stockLevel.availableSerialStock,
          onOrder: stockLevel.onOrder,
          committed: stockLevel.committed,
          total: stockLevel.total,

          eta: stockLevel.eta,
          etaStatus: stockLevel.etaStatus,

          averageMonthSales: null,
          thisMonth: null,
          oneMonthAgo: null,
          twoMonthsAgo: null,
          threeMonthsAgo: null,
          fourMonthsAgo: null,
          fiveMonthsAgo: null,
          sixMonthsAgo: null,
          sevenMonthsAgo: null,
          eightMonthsAgo: null,
          nineMonthsAgo: null,
          tenMonthsAgo: null,
          elevenMonthsAgo: null,

          findMatched: false,
          requestedQty: null,
          remainingQty: null,
          showRequestedQtyInput: false,
          isEdited: false,
          children: [] as StockDetailTableModel[],
          levelNumber: TableRowLevel.WarehouseLevelRow,
        } as StockDetailTableModel;
        output.push(noSalesRow);
      }
    });

    return output;
  }

  private getSalesProfitCentreLevelRows(allStockInfo: StockDetailTableModel[],
    requestedQtyData: ReorderEntryResponse[], procurementData: ProcurementLinesSearchResponse,
    allStockLevel: StockDetailTableModel[]): StockDetailTableModel[] {
    const profitCentreLevelRows: StockDetailTableModel[] = [];
    const profitCentreGroupData = groupBy(allStockInfo, 'salesProfitCentreCode');

    forEach(profitCentreGroupData, (group: StockDetailTableModel[], _key: string) => {
      const keyItem = group[0];
      const allStockInfoUnderSameProfitCentre = group;
      const childrenRows = this.getSalesBranchLevelRows(allStockInfoUnderSameProfitCentre, requestedQtyData, procurementData, allStockLevel);

      const availableStock = sumBy(childrenRows, g => g.availableStock as number);
      const availableSerialisedStock = sumBy(childrenRows, g => g.availableSerialStock || 0);
      const availableNonSerialisedStock = sumBy(childrenRows, g => g.availableNonSerialStock || 0);

      const onOrder = sumBy(childrenRows, g => g.onOrder || 0);
      const committed = sumBy(childrenRows, g => g.committed || 0);
      const total = availableStock + onOrder;
      const requestedQty = sumBy(childrenRows, g => g.requestedQty || 0);

      const averageMonthSales = sumBy(childrenRows, g => g.averageMonthSales || 0);
      const thisMonth = sumBy(childrenRows, g => g.thisMonth || 0);
      const oneMonthAgo = sumBy(childrenRows, g => g.oneMonthAgo || 0);
      const twoMonthsAgo = sumBy(childrenRows, g => g.twoMonthsAgo || 0);
      const threeMonthsAgo = sumBy(childrenRows, g => g.threeMonthsAgo || 0);
      const fourMonthsAgo = sumBy(childrenRows, g => g.fourMonthsAgo || 0);
      const fiveMonthsAgo = sumBy(childrenRows, g => g.fiveMonthsAgo || 0);
      const sixMonthsAgo = sumBy(childrenRows, g => g.sixMonthsAgo || 0);
      const sevenMonthsAgo = sumBy(childrenRows, g => g.sevenMonthsAgo || 0);
      const eightMonthsAgo = sumBy(childrenRows, g => g.eightMonthsAgo || 0);
      const nineMonthsAgo = sumBy(childrenRows, g => g.nineMonthsAgo || 0);
      const tenMonthsAgo = sumBy(childrenRows, g => g.tenMonthsAgo || 0);
      const elevenMonthsAgo = sumBy(childrenRows, g => g.elevenMonthsAgo || 0);

      const profitCentreLevelRow = {
        showBold: true,
        productId: keyItem.productId,
        productCode: keyItem.productCode,
        salesBranchCode: keyItem.salesProfitCentreCode, // profit centre header is on the "Sales Branch"
        stockBranchCode: '',
        stockWarehouseCode: '',

        availableStock: roundForDisplay(availableStock),
        availableSerialStock: roundForDisplay(availableSerialisedStock),
        availableNonSerialStock: roundForDisplay(availableNonSerialisedStock),
        onOrder: roundForDisplay(onOrder),
        committed: roundForDisplay(committed),
        total: roundForDisplay(total),

        averageMonthSales: roundForDisplay(averageMonthSales),
        thisMonth: roundForDisplay(thisMonth),
        oneMonthAgo: roundForDisplay(oneMonthAgo),
        twoMonthsAgo: roundForDisplay(twoMonthsAgo),
        threeMonthsAgo: roundForDisplay(threeMonthsAgo),
        fourMonthsAgo: roundForDisplay(fourMonthsAgo),
        fiveMonthsAgo: roundForDisplay(fiveMonthsAgo),
        sixMonthsAgo: roundForDisplay(sixMonthsAgo),
        sevenMonthsAgo: roundForDisplay(sevenMonthsAgo),
        eightMonthsAgo: roundForDisplay(eightMonthsAgo),
        nineMonthsAgo: roundForDisplay(nineMonthsAgo),
        tenMonthsAgo: roundForDisplay(tenMonthsAgo),
        elevenMonthsAgo: roundForDisplay(elevenMonthsAgo),

        findMatched: false,
        requestedQty: requestedQty > 0 ? requestedQty : null,
        remainingQty: null,
        isEdited: false,
        children: childrenRows,
        levelNumber: TableRowLevel.ProfitCentreLevelRow,
        eta: null,
      } as StockDetailTableModel;

      profitCentreLevelRows.push(profitCentreLevelRow);
    });

    let filterProfitCentreLevelRows = profitCentreLevelRows;
    const filterBySalesBranch = this.GetDetailsPageFilterBySalesBranch && this.GetUserBranches && this.GetUserBranches.length > 0;
    if (filterBySalesBranch) {
      profitCentreLevelRows.forEach(p => p.children = p.children.filter(c => this.GetUserBranches.includes(c.salesBranchCode)));
      filterProfitCentreLevelRows = profitCentreLevelRows.filter(p => p.children.length > 0);
    }

    return this.addProfitCentreLevelTotalRow(filterProfitCentreLevelRows);
  }

  private addProfitCentreLevelTotalRow(filterProfitCentreLevelRows: StockDetailTableModel[]): StockDetailTableModel[] {
    const totalProfitCentreLevelRow = {
      showBold: true,
      productId: 0,
      productCode: '',
      salesBranchCode: 'Total',
      stockBranchCode: '',
      stockWarehouseCode: '',

      availableStock: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.availableStock || 0)),
      availableSerialStock: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.availableSerialStock || 0)),
      availableNonSerialStock: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.availableNonSerialStock || 0)),
      onOrder: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.onOrder || 0)),
      committed: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.committed || 0)),
      total: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.total || 0)),

      averageMonthSales: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.averageMonthSales || 0)),
      thisMonth: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.thisMonth || 0)),
      oneMonthAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.oneMonthAgo || 0)),
      twoMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.twoMonthsAgo || 0)),
      threeMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.threeMonthsAgo || 0)),
      fourMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.fourMonthsAgo || 0)),
      fiveMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.fiveMonthsAgo || 0)),
      sixMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.sixMonthsAgo || 0)),
      sevenMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.sevenMonthsAgo || 0)),
      eightMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.eightMonthsAgo || 0)),
      nineMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.nineMonthsAgo || 0)),
      tenMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.tenMonthsAgo || 0)),
      elevenMonthsAgo: roundForDisplay(sumBy(filterProfitCentreLevelRows, x => x.elevenMonthsAgo || 0)),

      findMatched: false,
      requestedQty: null,
      remainingQty: null,
      isEdited: false,
      children: [] as StockDetailTableModel[],
      levelNumber: TableRowLevel.ProfitCentreLevelRow,
      eta: null,
    } as StockDetailTableModel;

    filterProfitCentreLevelRows.push(totalProfitCentreLevelRow);
    return filterProfitCentreLevelRows;
  }

  private getSalesBranchLevelRows(allStockInfoUnderSameProfitCentre: StockDetailTableModel[],
    requestedQtyData: ReorderEntryResponse[], procurementData: ProcurementLinesSearchResponse,
    allStockLevel: StockDetailTableModel[]): StockDetailTableModel[] {
    const branchLevelRows: StockDetailTableModel[] = [];
    const salesBranchGroupData = groupBy(allStockInfoUnderSameProfitCentre, 'salesBranchCode');
    const remainingQtys = this.mapRemainingQtys(procurementData);

    forEach(salesBranchGroupData, (group: StockDetailTableModel[]) => {
      const keyItem = group[0];
      const childrenRows = this.getSalesWarehouseLevelRows(group);
      const childrenRowCount = childrenRows.length;

      // stock request is branch level based
      const matchedRequestedQtyData = requestedQtyData.find(
        data => data.productId === keyItem.productId && data.branchCode === keyItem.salesBranchCode
      );

      const allStockInfoBranchSameAsSalesBranch = group.filter(x => x.salesBranchCode === x.stockBranchCode);
      let groupEta: string | null = null;
      let groupEtaStatus = '';
      forEach(allStockInfoBranchSameAsSalesBranch, item => {
        // take latest ETA
        if (item.eta && (!groupEta || moment(item.eta).isBefore(moment(groupEta)))) {
          groupEta = item.eta;
          groupEtaStatus = item.etaStatus;
        }
      });

      const showChildren = childrenRowCount > 1 || (childrenRowCount === 1 && childrenRows.every(x => x.isStockBranchDifferentFromSalesBranch === true));

      branchLevelRows.push({
        productId: keyItem.productId,
        productCode: keyItem.productCode,
        productGroupName: keyItem.productGroupName,
        salesProfitCentreCode: keyItem.salesProfitCentreCode,
        salesBranchCode: keyItem.salesBranchCode,
        stockBranchCode: childrenRowCount > 1 ? '' : keyItem.stockBranchCode, // empty displaying if have children rows
        stockWarehouseCode: childrenRowCount > 1 ? '' : keyItem.stockWarehouseCode, // empty displaying if have children rows
        stockClass: allStockLevel.find(x => x.stockWarehouseCode === keyItem.salesBranchCode)?.stockClass ?? { code: '', color: '' },

        availableStock: roundForDisplay(sumBy(allStockInfoBranchSameAsSalesBranch, x => x.availableStock || 0)),
        availableNonSerialStock: roundForDisplay(sumBy(allStockInfoBranchSameAsSalesBranch, x => x.availableNonSerialStock || 0)),
        availableSerialStock: roundForDisplay(sumBy(allStockInfoBranchSameAsSalesBranch, x => x.availableSerialStock || 0)),
        onOrder: toFixedNumber(sumBy(allStockInfoBranchSameAsSalesBranch, x => x.onOrder || 0), 0),
        committed: roundForDisplay(sumBy(allStockInfoBranchSameAsSalesBranch, x => x.committed || 0)),
        total: roundForDisplay(sumBy(allStockInfoBranchSameAsSalesBranch, x => x.total || 0)),
        eta: childrenRowCount > 1 ? null : groupEta,
        etaStatus: childrenRowCount > 1 ? null : groupEtaStatus,

        averageMonthSales: roundForDisplay(sumBy(group, x => x.averageMonthSales || 0)),
        thisMonth: roundForDisplay(sumBy(group, x => x.thisMonth || 0)),
        oneMonthAgo: roundForDisplay(sumBy(group, x => x.oneMonthAgo || 0)),
        twoMonthsAgo: roundForDisplay(sumBy(group, x => x.twoMonthsAgo || 0)),
        threeMonthsAgo: roundForDisplay(sumBy(group, x => x.threeMonthsAgo || 0)),
        fourMonthsAgo: roundForDisplay(sumBy(group, x => x.fourMonthsAgo || 0)),
        fiveMonthsAgo: roundForDisplay(sumBy(group, x => x.fiveMonthsAgo || 0)),
        sixMonthsAgo: roundForDisplay(sumBy(group, x => x.sixMonthsAgo || 0)),
        sevenMonthsAgo: roundForDisplay(sumBy(group, x => x.sevenMonthsAgo || 0)),
        eightMonthsAgo: roundForDisplay(sumBy(group, x => x.eightMonthsAgo || 0)),
        nineMonthsAgo: roundForDisplay(sumBy(group, x => x.nineMonthsAgo || 0)),
        tenMonthsAgo: roundForDisplay(sumBy(group, x => x.tenMonthsAgo || 0)),
        elevenMonthsAgo: roundForDisplay(sumBy(group, x => x.elevenMonthsAgo || 0)),

        findMatched: false,
        isEdited: false,
        children: showChildren ? childrenRows : [] as StockDetailTableModel[],
        levelNumber: TableRowLevel.BranchLevelRow,

        remainingQty: remainingQtys.get(keyItem.salesBranchCode)
          ? (remainingQtys.get(keyItem.salesBranchCode)?.remainingWeightTotalKG ?? 0) / 1000.0
          : null,
        requestedQty: matchedRequestedQtyData ? matchedRequestedQtyData.requestedQuantity : null,
        comment: matchedRequestedQtyData ? matchedRequestedQtyData.comment : '',
        showRequestedQtyInput: true,
      } as StockDetailTableModel);

    });

    return branchLevelRows;
  }

  private getSalesWarehouseLevelRows(allStockInfoUnderSameSalesBranch: StockDetailTableModel[]): StockDetailTableModel[] {
    const warehouseLevelRows: StockDetailTableModel[] = [];
    const stockWarehouseGroupData = groupBy(allStockInfoUnderSameSalesBranch.sort((a, b) => (a.stockWarehouseCode > b.stockWarehouseCode ? 1 : -1)), 'stockWarehouseCode');

    let warehouseSameAsSalesBranch;
    forEach(stockWarehouseGroupData, (group: StockDetailTableModel[]) => {
      const keyItem = group[0];

      const isStockBranchDifferentFromSalesBranch = keyItem.isStockBranchDifferentFromSalesBranch === true;
      const row = {
        productId: keyItem.productId,
        productCode: keyItem.productCode,
        productGroupName: keyItem.productGroupName,
        salesProfitCentreCode: '',
        salesBranchCode: '', // leave sales branch code empty for displaying
        stockBranchCode: keyItem.stockBranchCode,
        stockWarehouseCode: keyItem.stockWarehouseCode,
        stockClass: keyItem.stockClass,
        isStockBranchDifferentFromSalesBranch: keyItem.isStockBranchDifferentFromSalesBranch,

        availableStock: isStockBranchDifferentFromSalesBranch ? null : roundForDisplay(sumBy(group, x => x.availableStock || 0)),
        availableNonSerialStock: isStockBranchDifferentFromSalesBranch ? null : roundForDisplay(sumBy(group, x => x.availableNonSerialStock || 0)),
        availableSerialStock: isStockBranchDifferentFromSalesBranch ? null : roundForDisplay(sumBy(group, x => x.availableSerialStock || 0)),
        onOrder: isStockBranchDifferentFromSalesBranch ? null : toFixedNumber(sumBy(group, x => x.onOrder || 0), 0),
        committed: isStockBranchDifferentFromSalesBranch ? null : roundForDisplay(sumBy(group, x => x.committed || 0)),
        total: isStockBranchDifferentFromSalesBranch ? null : roundForDisplay(sumBy(group, x => x.total || 0)),
        eta: isStockBranchDifferentFromSalesBranch ? null : keyItem.eta,
        etaStatus: isStockBranchDifferentFromSalesBranch ? null : keyItem.etaStatus,

        averageMonthSales: roundForDisplay(sumBy(group, x => x.averageMonthSales || 0)),
        thisMonth: roundForDisplay(sumBy(group, x => x.thisMonth || 0)),
        oneMonthAgo: roundForDisplay(sumBy(group, x => x.oneMonthAgo || 0)),
        twoMonthsAgo: roundForDisplay(sumBy(group, x => x.twoMonthsAgo || 0)),
        threeMonthsAgo: roundForDisplay(sumBy(group, x => x.threeMonthsAgo || 0)),
        fourMonthsAgo: roundForDisplay(sumBy(group, x => x.fourMonthsAgo || 0)),
        fiveMonthsAgo: roundForDisplay(sumBy(group, x => x.fiveMonthsAgo || 0)),
        sixMonthsAgo: roundForDisplay(sumBy(group, x => x.sixMonthsAgo || 0)),
        sevenMonthsAgo: roundForDisplay(sumBy(group, x => x.sevenMonthsAgo || 0)),
        eightMonthsAgo: roundForDisplay(sumBy(group, x => x.eightMonthsAgo || 0)),
        nineMonthsAgo: roundForDisplay(sumBy(group, x => x.nineMonthsAgo || 0)),
        tenMonthsAgo: roundForDisplay(sumBy(group, x => x.tenMonthsAgo || 0)),
        elevenMonthsAgo: roundForDisplay(sumBy(group, x => x.elevenMonthsAgo || 0)),

        findMatched: false,
        isEdited: false,
        children: [] as StockDetailTableModel[],
        levelNumber: TableRowLevel.WarehouseLevelRow,
      } as StockDetailTableModel;

      // If the warehouse code matches the sales branch, set it as the top warehouse
      if (keyItem.stockWarehouseCode === keyItem.salesBranchCode) {
        warehouseSameAsSalesBranch = row;
      } else {
        warehouseLevelRows.push(row);
      }

    });

    if (warehouseSameAsSalesBranch) {
      return [warehouseSameAsSalesBranch].concat(warehouseLevelRows);
    }

    return warehouseLevelRows;
  }

  private mapRemainingQtys(
    procurementData: ProcurementLinesSearchResponse
  ): Map<string, ProcurementLine> {
    // Related to the other for the request to purchasing, we can probably remove this once we work out how to do the filtering in the azure search. This is
    // a temporary fix for sorting the return data.
    const lines = procurementData.results!.filter(
      (res) =>
        res.line!.lineState!.toLowerCase() !== 'completed' &&
        res.line!.lineState!.toLowerCase() !== 'cancelled' &&
        res.line!.lineState!.toLowerCase() !== 'deleted' &&
        res.line!.lineState!.toLowerCase() !== 'receipted'
    );
    const remainingQtys = new Map<string, any>();
    lines.forEach((line) => {
      const branch =
        procurementData.relatedData!.procurementOrders![
          parseInt(line!.procurementOrderDocumentNumber!)
        ].branchCode;
      if (
        !!remainingQtys.get(branch!) &&
        new Date(remainingQtys.get(branch!).etaDate) < new Date(line!.line!.etaDate!)
      ) {
        return;
      }

      remainingQtys.set(branch!, line.line);
    });

    return remainingQtys;
  }
}
